/**
 * Response gating for the Accounts views (design D14).
 *
 * A tenant tag alone is not enough: within one application a slow detail or
 * ledger response for account A can still land on account B's page, and a
 * superseded filter/preset/page response can still overwrite the current one.
 * Both show one user's money under another user's name.
 *
 * So every request is tagged with the **full** context that issued it —
 * application id, account id, preset view, the complete filter set, the page,
 * and a monotonic per-surface generation — and a response is applied only when
 * every element still matches the surface's current context *and* its
 * generation is the newest issued for that surface. Anything else is discarded
 * whole; it is never partially rendered.
 */

export type Surface = 'list' | 'detail' | 'ledger'

export interface RequestContext {
  applicationId: number
  /** null for the list surface, the account under view otherwise. */
  accountId: string | null
  /** Preset view for a ledger request; null elsewhere and for "all entries". */
  view: string | null
  /** The complete filter set as sent, so a dropped filter cannot pass as equal. */
  filters: Readonly<Record<string, string>>
  page: number
}

export interface TaggedRequest extends RequestContext {
  generation: number
}

/** Tag a request with the next generation for its surface. */
export function tagRequest(context: RequestContext, previousGeneration: number): TaggedRequest {
  return { ...context, generation: previousGeneration + 1 }
}

function sameFilters(
  a: Readonly<Record<string, string>>,
  b: Readonly<Record<string, string>>
): boolean {
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false
  return aKeys.every((key) => Object.prototype.hasOwnProperty.call(b, key) && a[key] === b[key])
}

/**
 * Whether a response tagged `tag` may be applied to a surface whose current
 * context is `current` and whose newest issued generation is `latestGeneration`.
 */
export function shouldApplyResponse(
  tag: TaggedRequest,
  current: RequestContext,
  latestGeneration: number
): boolean {
  if (tag.generation !== latestGeneration) return false
  if (tag.applicationId !== current.applicationId) return false
  if (tag.accountId !== current.accountId) return false
  if (tag.view !== current.view) return false
  if (tag.page !== current.page) return false
  return sameFilters(tag.filters, current.filters)
}
